import type { AdTemplate } from '@shared/schema';
import { adStyleOf } from './ad-style';

// The first row keeps the approved Caraway examples. The second row adds four
// distinct ways to tell an ad story, each represented by an existing global
// library image. IDs identify examples; the live library supplies the URL.
export const INSPIRATION_CATEGORIES = [
  { style: 'Social Proof', preferredId: 2194 },
  { style: 'Sale', preferredId: 2193 },
  { style: 'Us vs Them', preferredId: 2192 },
  { style: 'Product Launch', preferredId: 2191 },
  { style: 'Problem-Solution', preferredId: 2002 },
  { style: 'UGC Style', preferredId: 2187 },
  { style: 'Education', preferredId: 1754 },
  { style: 'How It Works', preferredId: 1240 },
] as const;

function isBunnyImage(template: AdTemplate): boolean {
  return template.isGlobal && template.mediaType !== 'video' && /^https:\/\/[^/]+\.b-cdn\.net\//i.test(template.imageUrl);
}

export function selectInspirationCards(templates: AdTemplate[]) {
  return INSPIRATION_CATEGORIES.flatMap(({ style, preferredId }) => {
    const matching = templates.filter(template => adStyleOf(template) === style && isBunnyImage(template));
    const template = matching.find(template => template.id === preferredId) ?? matching[0];
    return template ? [{ style, template }] : [];
  });
}

import type { InspirationTemplate } from '@shared/adTemplateMetadata';
export type HomeInspirationCard = { axis: 'style' | 'angle'; label: string; template: InspirationTemplate };
/** A mount seed gives a new sample on page load and stable results on refetch. */
export function selectHomeInspirationCards(templates: InspirationTemplate[], seed: number): HomeInspirationCard[] {
  let state = seed >>> 0;
  const random = () => {
    state += 0x6D2B79F5;
    let value = Math.imul(state ^ state >>> 15, state | 1);
    value ^= value + Math.imul(value ^ value >>> 7, value | 61);
    return ((value ^ value >>> 14) >>> 0) / 4294967296;
  };
  const shuffle = <T,>(values: T[]): T[] => {
    for (let i = values.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [values[i], values[j]] = [values[j], values[i]];
    }
    return values;
  };
  const ids = new Set<number>(), urls = new Set<string>();
  const categories = new Map<string, { axis: HomeInspirationCard['axis']; label: string; templates: InspirationTemplate[] }>();
  // Canonical input order makes identical query results stable even if reordered.
  for (const template of [...templates].sort((a, b) => a.id - b.id || a.imageUrl.localeCompare(b.imageUrl))) {
    if (!isBunnyImage(template) || ids.has(template.id) || urls.has(template.imageUrl)) continue;
    ids.add(template.id); urls.add(template.imageUrl);
    for (const axis of ['style', 'angle'] as const) {
      const label = (axis === 'style' ? template.sourceAdStyle : template.sourceAdAngle)?.trim();
      if (!label) continue;
      const key = `${axis}:${label.toLowerCase()}`;
      const category = categories.get(key) ?? { axis, label, templates: [] };
      category.templates.push(template);
      categories.set(key, category);
    }
  }
  const pool = shuffle([...categories.entries()].sort(([a], [b]) => a.localeCompare(b)));
  // Reserve a compatible pair first. A scarce angle may share its only image
  // with a style; choose a different style so both axes survive the sample.
  const styles = pool.filter(([, category]) => category.axis === 'style');
  const angles = pool.filter(([, category]) => category.axis === 'angle');
  let priority: typeof pool = [];
  for (const style of styles) {
    const angle = angles.find(([, category]) => style[1].templates.some(s => category.templates.some(a => s.id !== a.id)));
    if (angle) { priority = shuffle([style, angle]); break; }
  }
  const order = [...priority, ...pool.filter(c => !priority.includes(c))];
  for (const [, category] of order) shuffle(category.templates);
  const assigned = new Map<number, string>();
  const chosen = new Map<string, InspirationTemplate>();
  // Reassign shared style/angle images to find a full unique-image sample.
  const match = (key: string, visited: Set<number>): boolean => {
    for (const template of categories.get(key)!.templates) {
      if (visited.has(template.id)) continue;
      visited.add(template.id);
      const previous = assigned.get(template.id);
      if (!previous || match(previous, visited)) {
        assigned.set(template.id, key);
        chosen.set(key, template);
        return true;
      }
    }
    return false;
  };
  const selected: string[] = [];
  for (const [key] of order) {
    if (match(key, new Set())) selected.push(key);
    if (selected.length === 20) break;
  }
  return selected.map(key => ({ axis: categories.get(key)!.axis, label: categories.get(key)!.label, template: chosen.get(key)! }));
}
